/**
 * CardLadderLink — a deep link out to Card Ladder for one graded slab.
 *
 * Shell: any authenticated surface. Never `(kiosk)` — the link is gated on an
 * account setting read from staff-only state, and the destination is a
 * third-party pricing tool a customer has no reason to see mid-negotiation.
 *
 * vendor#1247 / vendor#1163 / vendor#1274. **Deep-link-only, forever** —
 * vendor#1159's ruling. Card Ladder 403s every automated request (homepage,
 * card pages, both search paths, even their Zendesk) with a full browser UA,
 * and the owner rejected a credentialed scraper on account-ban risk on
 * 2026-09-14. This component only ever builds a URL a human clicks; it never
 * fetches, parses, or stores anything from the other end of it.
 *
 * ## Two independent gates, and BOTH must pass or nothing renders
 *
 *   - **A destination must resolve.** {@link resolveCardLadderLink} tries the
 *     grade-accurate `sales-history` link first (`universalGemrateId` +
 *     `grader` + a parseable `grade`, all present), then the card-level
 *     `search` fallback (`gemrateId` alone), then gives up. There is no third
 *     shape — a link to the wrong grade is worse than no link at all
 *     (vendor#1274: this is the fix for the shipped defect where every
 *     `gemrateId`-only link opened on an arbitrary grade, because
 *     `gemrate_id` identifies a CARD, not a graded copy).
 *   - **`enabled` must be true.** The account-level "I have a Card Ladder
 *     account" setting (owner ruling, 2026-09-14) — default OFF, because
 *     Card Ladder's Sales History is a Pro-only feature and the link is
 *     worth little to a dealer who can't see it once they land there.
 *
 * ## Two presentations
 *
 *   - `'icon'` (default) — the compact `ActionIcon` this component always
 *     rendered, for dense surfaces (inventory rows, bin card rows, a slot
 *     inspector's identity block) where a labelled button would crowd every
 *     row. Its tooltip names the fallback when the link is card-level only,
 *     rather than silently promising a grade it cannot show.
 *   - `'button'` — a labelled Mantine `<Button>` sized to sit beside Move /
 *     Label / Edit-style action-row buttons (owner ruling, 2026-09-18: the
 *     icon-only treatment on the card detail page's cert line was too subtle
 *     — the owner hard-reloaded twice and could not find it). Used on the
 *     card detail page's top-right action row and graded lookup's page-header
 *     actions slot; every other surface keeps `'icon'`.
 *
 * There used to be a second `variant="text"` presentation (an inline
 * "View on Card Ladder" anchor), dropped as dead code (code-reviewer N1,
 * vendor#1247/#1163) because it had no design coverage and no call site. This
 * is a DIFFERENT addition: `'button'` exists because the product owner
 * explicitly ruled on its placement and shape (vendor#1274), not because a
 * leftover prop was reactivated.
 */
import { ActionIcon, Button, Tooltip } from '@mantine/core';
import { IconExternalLink } from '@tabler/icons-react';
import { resolveCardLadderLink, type CardLadderLinkInput } from './cardLadderUrl';

export interface CardLadderLinkProps extends CardLadderLinkInput {
  /** The account's "I have a Card Ladder account" setting. False = nothing
   *  renders, even with a resolvable link — see module docblock. */
  enabled: boolean;
  /** `'icon'` (default) for dense surfaces; `'button'` for a labelled action
   *  beside Move/Label/Edit-style buttons. See module docblock. */
  variant?: 'icon' | 'button';
}

/** See module docblock. Renders nothing unless `enabled` AND a destination
 *  resolves. */
export function CardLadderLink({
  gemrateId,
  universalGemrateId,
  grader,
  grade,
  enabled,
  variant = 'icon',
}: CardLadderLinkProps) {
  if (!enabled) return null;
  const resolved = resolveCardLadderLink({ gemrateId, universalGemrateId, grader, grade });
  if (!resolved) return null;

  // The fallback is real (a genuine deep link to the right card) but not the
  // verified grade — say so rather than letting the same tooltip/label imply
  // a precision this link does not have.
  const tooltipLabel = resolved.gradeAccurate
    ? 'View on Card Ladder'
    : 'View on Card Ladder (card only — grade not linked)';

  if (variant === 'button') {
    return (
      <Button
        component="a"
        href={resolved.url}
        target="_blank"
        rel="noopener noreferrer"
        size="xs"
        variant="default"
        h={34}
        leftSection={<IconExternalLink size={14} stroke={1.75} />}
        data-testid="card-ladder-link"
        title={tooltipLabel}
        onClick={(e) => e.stopPropagation()}
      >
        {resolved.gradeAccurate ? 'Card Ladder' : 'Card Ladder (no grade)'}
      </Button>
    );
  }

  return (
    <Tooltip label={tooltipLabel} withArrow>
      <ActionIcon
        component="a"
        href={resolved.url}
        target="_blank"
        rel="noopener noreferrer"
        variant="light"
        color="gray"
        size="sm"
        aria-label={tooltipLabel}
        data-testid="card-ladder-link"
        onClick={(e) => e.stopPropagation()}
      >
        <IconExternalLink size={13} stroke={1.75} />
      </ActionIcon>
    </Tooltip>
  );
}
